import React from "react";
import { Link } from "react-router-dom";
import {
  MdCampaign,
  MdFolderOpen,
  MdQuiz,
  MdTrendingUp,
  MdOutlineOpenInNew,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import CoursePricingBadge from "../../components/CoursePricingBadge";
import {
  instructorCourseFoldersService,
  SYSTEM_ROOT_FOLDER_TITLE,
} from "services/instructorCourseFoldersService";
import { instructorCourseAnnouncementsService } from "services/instructorCourseAnnouncementsService";
import { instructorQuizzesService } from "services/instructorQuizzesService";
import { Skeleton } from "components/ui/Skeleton";

type StatProps = {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  to: string;
  tone: "brand" | "indigo" | "emerald" | "amber";
};

const TONE: Record<StatProps["tone"], string> = {
  brand: "from-brand-400/20 to-brand-400/5 text-brand-600 dark:text-brand-200",
  indigo: "from-indigo-400/20 to-indigo-400/5 text-indigo-600 dark:text-indigo-200",
  emerald: "from-emerald-400/20 to-emerald-400/5 text-emerald-600 dark:text-emerald-200",
  amber: "from-amber-400/20 to-amber-400/5 text-amber-700 dark:text-amber-200",
};

function StatCard({ icon, label, value, to, tone }: StatProps) {
  return (
    <Link
      to={to}
      className={`group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br ${TONE[tone]} p-4 shadow-sm transition-transform hover:-translate-y-0.5 dark:border-white/10`}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/80 shadow-sm dark:bg-navy-800">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
      </div>
      <MdOutlineOpenInNew className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

export default function OverviewPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { course, counts, setCounts } = useCourse();

  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchCounts() {
      setLoading(true);
      try {
        const [folders, announcements, quizzes] = await Promise.all([
          instructorCourseFoldersService.list(course.id),
          instructorCourseAnnouncementsService.list(course.id, { limit: 1 }),
          instructorQuizzesService.listForCourse(course.id),
        ]);
        if (cancelled) return;
        // The system root is an implementation detail used for items added
        // directly to the course. It is not a folder created by the teacher.
        const foldersCount = folders.data.filter(
          (folder) => folder.title !== SYSTEM_ROOT_FOLDER_TITLE
        ).length;
        const itemsCount = folders.data.reduce((acc, f) => acc + (f.items_count || 0), 0);
        const announcementsCount = announcements.meta?.total ?? announcements.data.length;
        setCounts({
          folders: foldersCount,
          items: itemsCount,
          quizzes: quizzes.length,
          announcements: announcementsCount,
        });
      } catch {
        // silent; toasts from interceptor already shown
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [course.id, setCounts]);

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="mb-3 flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white">
            {isArabic ? "نظرة سريعة" : "At a glance"}
          </h2>
          <CoursePricingBadge course={course} isArabic={isArabic} />
        </div>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={<MdFolderOpen className="h-6 w-6" />}
              label={isArabic ? "المجلدات" : "Folders"}
              value={counts.folders ?? 0}
              to={`../content`}
              tone="brand"
            />
            <StatCard
              icon={<MdTrendingUp className="h-6 w-6" />}
              label={isArabic ? "العناصر" : "Items"}
              value={counts.items ?? 0}
              to={`../content`}
              tone="emerald"
            />
            <StatCard
              icon={<MdQuiz className="h-6 w-6" />}
              label={isArabic ? "الاختبارات" : "Quizzes"}
              value={counts.quizzes ?? 0}
              to={`../quizzes`}
              tone="indigo"
            />
            <StatCard
              icon={<MdCampaign className="h-6 w-6" />}
              label={isArabic ? "الإعلانات" : "Announcements"}
              value={counts.announcements ?? 0}
              to={`../announcements`}
              tone="amber"
            />
          </div>
        )}
      </section>

    </div>
  );
}
